import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, CheckCircle2, XCircle, Search, RefreshCw, ExternalLink, Lock, Pencil, UserPlus, KeyRound, Copy, Check, ChevronDown, ChevronRight, X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useNavigate } from "react-router-dom";

import PasswordResetTicketsPanel from "@/components/PasswordResetTicketsPanel";

const TABS = [
  { id: "dashboard", label: "Tableau de bord", color: "rouge" },
  { id: "pending", label: "En attente", color: "amber" },
  { id: "approved", label: "Partenaires", color: "green" },
  { id: "staff", label: "Équipe Pythagore", color: "rouge" },
  { id: "rejected", label: "Refusés", color: "zinc" },
  { id: "companies", label: "Par entreprise", color: "rouge" },
];

const NSE_REQUIRED = [1, 2, 3];

const PARTNER_LEVELS = [
  { value: "Advocate", label: "Advocate", hw: 25, sw: 15 },
  { value: "Select",   label: "Select",   hw: 30, sw: 20 },
  { value: "Advanced", label: "Advanced", hw: 35, sw: 25 },
  { value: "Expert",   label: "Expert",   hw: 45, sw: 30 },
];

function StatusBadge({ status }) {
  const map = {
    pending: { label: "En attente", className: "bg-amber-100 text-amber-800" },
    approved: { label: "Approuvé", className: "bg-green-100 text-green-800" },
    rejected: { label: "Refusé", className: "bg-zinc-200 text-zinc-700" },
  };
  const s = map[status] || { label: status, className: "bg-zinc-100 text-zinc-700" };
  return (
    <span className={`inline-block px-2 py-0.5 text-[11px] rounded-sm font-semibold ${s.className}`}>
      {s.label}
    </span>
  );
}

function NseBadges({ nse }) {
  const set = new Set(nse || []);
  return (
    <div className="flex flex-wrap gap-1">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => {
        const has = set.has(n);
        const required = NSE_REQUIRED.includes(n);
        return (
          <span
            key={n}
            className={`px-1.5 py-0.5 text-[10px] rounded-sm font-mono ${
              has
                ? "bg-green-50 text-green-700 border border-green-200"
                : required
                  ? "bg-red-50 text-red-700 border border-red-200"
                  : "bg-zinc-50 text-zinc-400 border border-zinc-200"
            }`}
            title={`NSE ${n}${required ? " (requis)" : ""}${has ? " - validé" : " - non validé"}`}
          >
            {n}
          </span>
        );
      })}
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [actingId, setActingId] = useState(null);
  const [approveDialogUser, setApproveDialogUser] = useState(null);
  const [approveLevel, setApproveLevel] = useState("Advocate");
  const [editLevelUser, setEditLevelUser] = useState(null);
  const [editLevel, setEditLevel] = useState("Advocate");

  // Create internal account form (admins / reviewers).
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: "", password: "", full_name: "", company: "Pythagore", role: "reviewer",
  });
  const [creating, setCreating] = useState(false);

  // Reset password modal — shows the freshly generated one-shot temp password.
  const [resetResult, setResetResult] = useState(null); // { email, temp_password, issued_at }
  const [resettingId, setResettingId] = useState(null);
  const [resetting2FAId, setResetting2FAId] = useState(null);
  // « Par entreprise » view — populated from /admin/users/by-company
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [copied, setCopied] = useState(false);
  // Sub-filter scoped to the « Équipe Pythagore » staff tab.
  // "all" | "admins" | "reviewers"
  const [staffRoleFilter, setStaffRoleFilter] = useState("all");

  const resetPassword = async (u) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${u.full_name} (${u.email}) ?\n\nUn nouveau mot de passe temporaire sera généré. Vous devrez le transmettre vous-même au collaborateur — il ne pourra pas être récupéré ensuite.`)) {
      return;
    }
    setResettingId(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/reset_password`);
      setResetResult(r.data);
      setCopied(false);
      toast.success("Nouveau mot de passe généré");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la réinitialisation");
    } finally {
      setResettingId(null);
    }
  };

  const copyTempPassword = async () => {
    if (!resetResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temp_password);
      setCopied(true);
      toast.success("Mot de passe copié");
    } catch (_e) {
      toast.error("Impossible de copier — sélectionnez et copiez manuellement");
    }
  };

  const reset2FA = async (u) => {
    if (!window.confirm(`Réinitialiser la 2FA de ${u.full_name} (${u.email}) ?\n\nLe second facteur sera désactivé. La personne devra reconfigurer une nouvelle clé via /profile dès sa prochaine connexion.`)) {
      return;
    }
    setResetting2FAId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/reset-2fa`);
      toast.success("2FA réinitialisée");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la réinitialisation 2FA");
    } finally {
      setResetting2FAId(null);
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password || !createForm.full_name) {
      toast.error("E-mail, mot de passe et nom complet sont obligatoires.");
      return;
    }
    if (createForm.password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/users/create", createForm);
      const roleLabels = { admin: "Admin", reviewer: "Reviewer", super_admin: "Super Admin" };
      toast.success(`${roleLabels[createForm.role] || "Compte"} créé · ${createForm.email}`);
      setCreateForm({ email: "", password: "", full_name: "", company: "Pythagore", role: "reviewer" });
      setCreateOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la création");
    } finally {
      setCreating(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const [pendingR, approvedR, rejectedR] = await Promise.all([
        api.get("/admin/users", { params: { status: "pending" } }),
        api.get("/admin/users", { params: { status: "approved" } }),
        api.get("/admin/users", { params: { status: "rejected" } }),
      ]);
      // "Approuvés" → strict partners only (admin + reviewer accounts moved
      // to their own "Équipe Pythagore" tab so the partner roster stays clean).
      const isStaff = (u) => u.is_admin || u.is_fortinet_reviewer;
      const partners = approvedR.data.filter((u) => !isStaff(u));
      const staff = approvedR.data.filter(isStaff);
      setCounts({
        pending: pendingR.data.length,
        approved: partners.length,
        staff: staff.length,
        rejected: rejectedR.data.length,
      });
      const pick = {
        pending: pendingR.data,
        approved: partners,
        staff,
        rejected: rejectedR.data,
      }[tab];
      if (pick) setUsers(pick);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du chargement");
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const r = await api.get("/admin/users/by-company");
      setCompanies(r.data?.companies || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du chargement");
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    if (tab === "dashboard") return;  // dashboard tab fetches its own data
    refresh();
    if (tab === "companies") loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user?.is_admin]);

  const filteredUsers = useMemo(() => {
    let base = users;
    // Scope role filter to the staff tab so it's not silently applied
    // to other lists when the user switches tabs.
    if (tab === "staff" && staffRoleFilter !== "all") {
      base = base.filter((u) => {
        if (staffRoleFilter === "super")     return u.is_admin && u.is_fortinet_reviewer;
        if (staffRoleFilter === "admins")    return u.is_admin && !u.is_fortinet_reviewer;
        if (staffRoleFilter === "reviewers") return u.is_fortinet_reviewer && !u.is_admin;
        return true;
      });
    }
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter((u) =>
      [u.email, u.full_name, u.company, u.partner_portal_id].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [users, search, tab, staffRoleFilter]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="admin-forbidden">
        <ShieldCheck className="h-12 w-12 text-zinc-400 mx-auto" />
        <h1 className="mt-4 text-xl font-semibold text-zinc-900">Réservé aux administrateurs</h1>
        <p className="mt-2 text-sm text-zinc-600">Vous n'avez pas les droits requis pour accéder à cette page.</p>
      </div>
    );
  }

  const eligibilityIssues = (u) => {
    const out = [];
    if (!u.partner_portal_id) out.push("Pas d'ID PartnerPortal");
    const has = new Set(u.nse_certifications || []);
    const missing = NSE_REQUIRED.filter((n) => !has.has(n));
    if (missing.length) out.push(`Manque NSE ${missing.join(", ")}`);
    return out;
  };

  const approve = (u) => {
    // Open the approve dialog so the admin freezes the partner level.
    setApproveLevel(u.partner_level || "Advocate");
    setApproveDialogUser(u);
  };

  const confirmApprove = async (withLevel) => {
    const u = approveDialogUser;
    if (!u) return;
    setActingId(u.id);
    try {
      const payload = withLevel ? { partner_level: approveLevel } : {};
      await api.post(`/admin/users/${u.id}/approve`, payload);
      toast.success(
        withLevel
          ? `Compte approuvé · niveau ${approveLevel} figé`
          : "Compte approuvé · niveau partenaire non défini"
      );
      setApproveDialogUser(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally {
      setActingId(null);
    }
  };

  const openEditLevel = (u) => {
    setEditLevel(u.partner_level || "Advocate");
    setEditLevelUser(u);
  };

  const saveEditLevel = async (clear = false) => {
    const u = editLevelUser;
    if (!u) return;
    setActingId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/partner_level`, {
        partner_level: clear ? null : editLevel,
      });
      toast.success(clear ? "Niveau partenaire effacé" : `Niveau ${editLevel} appliqué`);
      setEditLevelUser(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally {
      setActingId(null);
    }
  };

  const reject = async (u) => {
    const reason = window.prompt(`Motif du refus pour ${u.email} ? (optionnel)`);
    if (reason === null) return;
    setActingId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/reject`, { reason });
      toast.success("Compte refusé");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally {
      setActingId(null);
    }
  };

  const toggleReviewer = async (u) => {
    const next = !u.is_fortinet_reviewer;
    setActingId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/toggle_reviewer`, { is_fortinet_reviewer: next });
      toast.success(next ? `${u.email} est désormais Reviewer Fortinet` : `Rôle Reviewer retiré à ${u.email}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] text-[#D42B1E] uppercase">Administration</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mt-1">
            Validation des inscriptions
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
            Vérifiez l'identifiant PartnerPortal et les certifications NSE 1, 2, 3 avant d'approuver chaque demande.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading} className="rounded-sm" data-testid="admin-refresh-button">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </Button>
      </div>

      {/* Create internal account (Admin / Reviewer) — collapsed by default */}
      <div className="mb-6 border border-zinc-200 bg-white rounded-sm" data-testid="admin-create-card">
        <button
          type="button"
          onClick={() => setCreateOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-zinc-50 transition-colors"
          data-testid="admin-create-toggle"
        >
          <div className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-[#D42B1E]" />
            <span className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">
              Créer un compte interne (Admin / Reviewer)
            </span>
          </div>
          <span className="text-[11px] text-zinc-400">{createOpen ? "Replier" : "Déplier"}</span>
        </button>
        {createOpen && (
          <form onSubmit={submitCreate} className="border-t border-zinc-100 p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-create-form">
            <div className="sm:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { value: "reviewer", label: "Reviewer Fortinet", desc: "Accès à la file de revue + chat sur les BOM partenaires." },
                { value: "admin", label: "Administrateur", desc: "Validation des inscriptions + gestion complète." },
                { value: "super_admin", label: "Super Admin", desc: "Admin ET Reviewer en même temps — combine les deux rôles." },
              ].map((r) => {
                const active = createForm.role === r.value;
                return (
                  <button
                    type="button"
                    key={r.value}
                    onClick={() => setCreateForm((f) => ({ ...f, role: r.value }))}
                    className={`text-left px-3 py-2.5 rounded-sm border transition-colors ${
                      active ? "border-[#D42B1E] bg-[#D42B1E]/5" : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid={`admin-create-role-${r.value}`}
                  >
                    <div className={`text-sm font-semibold ${active ? "text-[#D42B1E]" : "text-zinc-900"}`}>{r.label}</div>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{r.desc}</p>
                  </button>
                );
              })}
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-700 block mb-1">Nom complet *</label>
              <Input
                value={createForm.full_name}
                onChange={(e) => setCreateForm((f) => ({ ...f, full_name: e.target.value }))}
                placeholder="Jane Reviewer"
                className="rounded-sm"
                data-testid="admin-create-full-name"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-700 block mb-1">Société</label>
              <Input
                value={createForm.company}
                onChange={(e) => setCreateForm((f) => ({ ...f, company: e.target.value }))}
                placeholder="Pythagore"
                className="rounded-sm"
                data-testid="admin-create-company"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-700 block mb-1">E-mail *</label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="prenom.nom@pythagore.com"
                className="rounded-sm font-mono text-sm"
                data-testid="admin-create-email"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-700 block mb-1">
                Mot de passe initial * <span className="text-zinc-400 font-normal">(≥ 8 caractères)</span>
              </label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="••••••••"
                className="rounded-sm"
                data-testid="admin-create-password"
              />
            </div>
            <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-1">
              <Button
                type="submit"
                disabled={creating}
                className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm"
                data-testid="admin-create-submit"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {creating ? "Création…" : `Créer le compte ${
                  createForm.role === "admin" ? "Admin" :
                  createForm.role === "super_admin" ? "Super Admin" : "Reviewer"
                }`}
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Password reset tickets — admin reviews and resolves out-of-band */}
      <PasswordResetTicketsPanel />

      {/* Tabs */}
      <div className="border-b border-zinc-200 flex gap-1" data-testid="admin-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          const n = counts[t.id];
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active ? "border-[#D42B1E] text-[#D42B1E]" : "border-transparent text-zinc-600 hover:text-zinc-900"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label} <span className="ml-1 px-1.5 py-0.5 text-[11px] rounded-sm bg-zinc-100 text-zinc-700 font-mono">{n}</span>
            </button>
          );
        })}
      </div>

      {/* Search — hidden on the dashboard tab where it's not relevant */}
      {tab !== "dashboard" && (
        <div className="mt-4 mb-3 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            placeholder="Rechercher email, nom, société, PartnerPortal ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-sm"
            data-testid="admin-search-input"
          />
        </div>
      )}

      {/* Staff role quick filter — only on Équipe Pythagore tab */}
      {tab === "staff" && (
        <div className="mb-3 flex items-center gap-2" data-testid="admin-staff-role-filter">
          <span className="text-[11px] uppercase tracking-wide text-zinc-500 font-semibold mr-1">
            Filtrer par rôle :
          </span>
          {[
            { id: "all",         label: "Tous",                count: users.length },
            { id: "super",       label: "Super Admins",        count: users.filter((u) => u.is_admin && u.is_fortinet_reviewer).length },
            { id: "admins",      label: "Administrateurs",     count: users.filter((u) => u.is_admin && !u.is_fortinet_reviewer).length },
            { id: "reviewers",   label: "Reviewers Fortinet",  count: users.filter((u) => u.is_fortinet_reviewer && !u.is_admin).length },
          ].map((f) => {
            const active = staffRoleFilter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setStaffRoleFilter(f.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                  active
                    ? "bg-[#D42B1E] text-white border-[#D42B1E]"
                    : "bg-white text-zinc-700 border-zinc-300 hover:border-[#D42B1E]/50 hover:text-[#D42B1E]"
                }`}
                data-testid={`admin-staff-filter-${f.id}`}
              >
                {f.label}
                <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-mono ${
                  active ? "bg-white/20 text-white" : "bg-zinc-100 text-zinc-600"
                }`}>
                  {f.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Users table — pending / approved / rejected views OR companies view */}
      {tab === "dashboard" ? (
        <DashboardView />
      ) : tab === "companies" ? (
        <CompaniesView
          companies={companies}
          loading={loadingCompanies}
          search={search}
          onReset2FA={reset2FA}
          resetting2FAId={resetting2FAId}
        />
      ) : (
      <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Chargement…</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">
            {tab === "staff"
              ? "Aucun admin ni reviewer enregistré."
              : tab === "approved"
                ? "Aucun partenaire approuvé pour l'instant."
                : "Aucun utilisateur dans cette catégorie."}
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid={`admin-users-table-${tab}`}>
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-[11px] uppercase tracking-wide text-zinc-600">
                <th className="text-left px-4 py-2.5 font-semibold">Demandeur</th>
                {tab === "staff" ? (
                  <th className="text-left px-4 py-2.5 font-semibold">Rôle</th>
                ) : (
                  <>
                    <th className="text-left px-4 py-2.5 font-semibold">PartnerPortal</th>
                    <th className="text-left px-4 py-2.5 font-semibold">NSE 1-8</th>
                  </>
                )}
                <th className="text-left px-4 py-2.5 font-semibold">Niveau partenaire</th>
                <th className="text-left px-4 py-2.5 font-semibold">Statut</th>
                <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                {tab !== "staff" && (
                  <th className="text-center px-4 py-2.5 font-semibold">Reviewer FNT</th>
                )}
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filteredUsers.map((u) => {
                const issues = eligibilityIssues(u);
                return (
                  <tr key={u.id} className="hover:bg-zinc-50 align-top" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-zinc-900">{u.full_name}</div>
                      <div className="text-xs text-zinc-600 font-mono">{u.email}</div>
                      {u.company && <div className="text-[11px] text-zinc-500 mt-0.5">{u.company}</div>}
                    </td>
                    {tab === "staff" ? (
                      <td className="px-4 py-3">
                        {u.is_admin && u.is_fortinet_reviewer ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide rounded-sm font-semibold bg-gradient-to-r from-zinc-900 to-[#D42B1E] text-white">
                            <ShieldCheck className="h-3 w-3" /> Super Admin
                          </span>
                        ) : u.is_admin ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide rounded-sm font-semibold bg-zinc-900 text-white">
                            <ShieldCheck className="h-3 w-3" /> Administrateur
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wide rounded-sm font-semibold bg-amber-500 text-white">
                            <ShieldCheck className="h-3 w-3" /> Reviewer Fortinet
                          </span>
                        )}
                      </td>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-xs">
                          {u.partner_portal_id ? (
                            <a
                              href={`https://support.fortinet.com/AccountManagement/MyAccount.aspx`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-[#D42B1E] inline-flex items-center gap-1 hover:underline"
                            >
                              {u.partner_portal_id}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-red-600 font-medium">— manquant</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <NseBadges nse={u.nse_certifications} />
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3">
                      {u.partner_level ? (
                        <div className="flex items-center gap-1.5" data-testid={`admin-partner-level-${u.id}`}>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-sm font-mono font-semibold bg-[#D42B1E] text-white">
                            {u.partner_level_locked && <Lock className="h-2.5 w-2.5" />}
                            {u.partner_level}
                          </span>
                          {(tab === "approved" || tab === "staff") && (
                            <button
                              type="button"
                              onClick={() => openEditLevel(u)}
                              className="p-1 rounded-sm text-zinc-400 hover:text-zinc-900"
                              data-testid={`admin-edit-level-${u.id}`}
                              title="Modifier le niveau partenaire"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      ) : (tab === "approved" || tab === "staff") ? (
                        <button
                          type="button"
                          onClick={() => openEditLevel(u)}
                          className="text-[11px] text-zinc-500 underline underline-offset-2 hover:text-[#D42B1E]"
                          data-testid={`admin-set-level-${u.id}`}
                        >
                          Définir…
                        </button>
                      ) : (
                        <span className="text-[11px] text-zinc-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={u.status} />
                      {issues.length > 0 && tab === "pending" && (
                        <div className="mt-1 text-[10px] text-red-600">
                          {issues.join(" · ")}
                        </div>
                      )}
                      {u.rejected_reason && tab === "rejected" && (
                        <div className="mt-1 text-[10px] text-zinc-600 italic">« {u.rejected_reason} »</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-600 font-mono">
                      {(u.created_at || "").slice(0, 10)}
                    </td>
                    {tab !== "staff" && (
                      <td className="px-4 py-3 text-center">
                        {u.status === "approved" ? (
                          <button
                            type="button"
                            onClick={() => toggleReviewer(u)}
                            disabled={actingId === u.id}
                            aria-pressed={!!u.is_fortinet_reviewer}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-semibold uppercase tracking-wide border transition-colors ${
                              u.is_fortinet_reviewer
                                ? "bg-[#D42B1E] text-white border-[#D42B1E] hover:bg-[#B32218]"
                                : "bg-white text-zinc-600 border-zinc-300 hover:border-[#D42B1E]/50 hover:text-[#D42B1E]"
                            }`}
                            title={u.is_fortinet_reviewer ? "Retirer le rôle Reviewer Fortinet" : "Promouvoir Reviewer Fortinet"}
                            data-testid={`admin-toggle-reviewer-${u.id}`}
                          >
                            {u.is_fortinet_reviewer ? "✓ Reviewer" : "Promouvoir"}
                          </button>
                        ) : (
                          <span className="text-[11px] text-zinc-400">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      {u.status !== "approved" && (
                        <Button
                          size="sm"
                          onClick={() => approve(u)}
                          disabled={actingId === u.id || issues.length > 0}
                          className="bg-green-600 hover:bg-green-700 rounded-sm h-8 mr-2"
                          data-testid={`admin-approve-${u.id}`}
                          title={issues.length > 0 ? `Inéligible : ${issues.join(" · ")}` : "Approuver"}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                          Approuver
                        </Button>
                      )}
                      {u.status === "approved" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resetPassword(u)}
                          disabled={resettingId === u.id}
                          className="rounded-sm h-8 border-amber-300 text-amber-700 hover:bg-amber-50 mr-2"
                          title="Réinitialiser le mot de passe — utile quand un Account Manager change ou quitte la WAF"
                          data-testid={`admin-reset-password-${u.id}`}
                        >
                          <KeyRound className="h-3.5 w-3.5 mr-1" />
                          {resettingId === u.id ? "..." : "Réinitialiser MDP"}
                        </Button>
                      )}
                      {u.status === "approved" && u.totp_enabled && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reset2FA(u)}
                          disabled={resetting2FAId === u.id}
                          className="rounded-sm h-8 border-rose-300 text-rose-700 hover:bg-rose-50 mr-2"
                          title="Réinitialiser la 2FA — quand l'utilisateur a perdu son téléphone / changé d'app authenticator"
                          data-testid={`admin-reset-2fa-${u.id}`}
                        >
                          <ShieldOff className="h-3.5 w-3.5 mr-1" />
                          {resetting2FAId === u.id ? "..." : "Reset 2FA"}
                        </Button>
                      )}
                      {u.status !== "rejected" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reject(u)}
                          disabled={actingId === u.id}
                          className="rounded-sm h-8 border-zinc-300 text-zinc-700"
                          data-testid={`admin-reject-${u.id}`}
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" />
                          Refuser
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
      )}

      {/* Approve dialog with partner level selector */}
      {approveDialogUser && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setApproveDialogUser(null)}
          data-testid="approve-dialog-backdrop"
        >
          <div
            className="bg-white rounded-sm shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="approve-dialog"
          >
            <div className="px-5 py-4 border-b border-zinc-100">
              <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#D42B1E]">
                Approbation
              </p>
              <h3 className="mt-1 text-base font-semibold text-zinc-900">
                {approveDialogUser.full_name}
              </h3>
              <p className="text-xs text-zinc-600 font-mono mt-0.5">{approveDialogUser.email}</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-700 block mb-1.5">
                  Niveau partenaire à figer
                </label>
                <p className="text-[11px] text-zinc-500 mb-2 leading-relaxed">
                  Le niveau choisi sera <span className="font-semibold">verrouillé</span> sur le compte. Toutes les BOM/BOQ générées par ce partenaire appliqueront automatiquement les remises associées.
                </p>
                <div className="grid grid-cols-2 gap-2" data-testid="approve-level-grid">
                  {PARTNER_LEVELS.map((p) => {
                    const active = approveLevel === p.value;
                    return (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setApproveLevel(p.value)}
                        className={`text-left px-3 py-2 rounded-sm border transition-colors ${
                          active
                            ? "border-[#D42B1E] bg-[#D42B1E]/5"
                            : "border-zinc-200 hover:border-zinc-300"
                        }`}
                        data-testid={`approve-level-${p.value}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-semibold ${active ? "text-[#D42B1E]" : "text-zinc-900"}`}>
                            {p.label}
                          </span>
                          {active && <Lock className="h-3 w-3 text-[#D42B1E]" />}
                        </div>
                        <p className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                          HW −{p.hw}% · SW −{p.sw}%
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-2 bg-zinc-50/40">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setApproveDialogUser(null)}
                className="rounded-sm text-zinc-600"
                data-testid="approve-dialog-cancel"
              >
                Annuler
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => confirmApprove(false)}
                  disabled={actingId === approveDialogUser.id}
                  className="rounded-sm border-zinc-300"
                  data-testid="approve-dialog-skip-level"
                >
                  Approuver sans niveau
                </Button>
                <Button
                  size="sm"
                  onClick={() => confirmApprove(true)}
                  disabled={actingId === approveDialogUser.id}
                  className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm"
                  data-testid="approve-dialog-confirm"
                >
                  <Lock className="h-3.5 w-3.5 mr-1.5" />
                  Approuver et figer {approveLevel}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset-password result modal — shown once after a successful reset.
          The temp password cannot be retrieved later, so we make it easy to
          copy and remind the admin to forward it. */}
      {resetResult && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          data-testid="admin-reset-password-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setResetResult(null); }}
        >
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6 border border-zinc-200">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound className="h-5 w-5 text-amber-600" />
              <h3 className="text-base font-semibold text-zinc-900">Mot de passe temporaire généré</h3>
            </div>
            <p className="text-sm text-zinc-600">
              Un nouveau mot de passe a été généré pour <span className="font-mono text-zinc-900">{resetResult.email}</span>.
              <strong className="block mt-1 text-zinc-900">Copiez-le maintenant — il ne pourra pas être affiché à nouveau.</strong>
            </p>
            <div className="mt-4 flex items-center gap-2 border border-amber-300 bg-amber-50 rounded-sm p-3">
              <code className="flex-1 font-mono text-sm text-zinc-900 break-all select-all" data-testid="admin-reset-password-value">
                {resetResult.temp_password}
              </code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={copyTempPassword}
                className="shrink-0 rounded-sm border-amber-400 text-amber-800 hover:bg-amber-100"
                data-testid="admin-reset-password-copy"
              >
                {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                {copied ? "Copié" : "Copier"}
              </Button>
            </div>
            <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
              Transmettez ce mot de passe au collaborateur via un canal sécurisé (Outlook chiffré, Teams direct…). Demandez-lui de le changer dès la première connexion.
            </p>
            <div className="mt-5 flex justify-end">
              <Button
                onClick={() => setResetResult(null)}
                className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm"
                data-testid="admin-reset-password-close"
              >
                J'ai copié, fermer
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Edit partner level dialog (for already approved users) */}
      {editLevelUser && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setEditLevelUser(null)}
          data-testid="edit-level-dialog-backdrop"
        >
          <div
            className="bg-white rounded-sm shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-level-dialog"
          >
            <div className="px-5 py-4 border-b border-zinc-100">
              <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#D42B1E]">
                Niveau partenaire
              </p>
              <h3 className="mt-1 text-base font-semibold text-zinc-900">
                {editLevelUser.full_name}
              </h3>
              <p className="text-xs text-zinc-600 font-mono mt-0.5">{editLevelUser.email}</p>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-2">
                {PARTNER_LEVELS.map((p) => {
                  const active = editLevel === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setEditLevel(p.value)}
                      className={`text-left px-3 py-2 rounded-sm border transition-colors ${
                        active
                          ? "border-[#D42B1E] bg-[#D42B1E]/5"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      data-testid={`edit-level-${p.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-semibold ${active ? "text-[#D42B1E]" : "text-zinc-900"}`}>
                          {p.label}
                        </span>
                        {active && <Lock className="h-3 w-3 text-[#D42B1E]" />}
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                        HW −{p.hw}% · SW −{p.sw}%
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-2 bg-zinc-50/40">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => saveEditLevel(true)}
                disabled={actingId === editLevelUser.id || !editLevelUser.partner_level}
                className="rounded-sm text-red-600 hover:text-red-700"
                data-testid="edit-level-clear"
              >
                Effacer le niveau
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditLevelUser(null)}
                  className="rounded-sm border-zinc-300"
                  data-testid="edit-level-cancel"
                >
                  Annuler
                </Button>
                <Button
                  size="sm"
                  onClick={() => saveEditLevel(false)}
                  disabled={actingId === editLevelUser.id}
                  className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm"
                  data-testid="edit-level-save"
                >
                  <Lock className="h-3.5 w-3.5 mr-1.5" />
                  Figer {editLevel}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * « Par entreprise » view — accordion of companies grouped by email domain,
 * each one expanding into 3 role tables (Admin · Reviewer · Partner) when
 * opened. Auto-attaches at registration time keep this view tidy: every
 * member of an existing corporate domain is bucketed automatically.
 */
function CompaniesView({ companies, loading, search, onReset2FA, resetting2FAId }) {
  const [openKeys, setOpenKeys] = useState(new Set());
  const toggle = (k) => setOpenKeys((prev) => {
    const next = new Set(prev);
    if (next.has(k)) { next.delete(k); } else { next.add(k); }
    return next;
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return companies;
    const q = search.toLowerCase();
    return companies.filter((c) => {
      const haystack = [
        c.company, c.email_domain,
        ...Object.values(c.members_by_role || {}).flat().map((m) => `${m.email} ${m.full_name}`),
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [companies, search]);

  if (loading) {
    return <div className="border border-zinc-200 bg-white rounded-sm p-8 text-center text-sm text-zinc-500">Chargement…</div>;
  }
  if (!filtered.length) {
    return <div className="border border-zinc-200 bg-white rounded-sm p-8 text-center text-sm text-zinc-500">Aucune entreprise trouvée.</div>;
  }

  return (
    <div className="space-y-3" data-testid="admin-companies-view">
      {filtered.map((c) => {
        const isOpen = openKeys.has(c.company_key);
        const totalRoles = (c.members_by_role.admin.length > 0 ? 1 : 0) +
          (c.members_by_role.reviewer.length > 0 ? 1 : 0) +
          (c.members_by_role.partner.length > 0 ? 1 : 0);
        return (
          <div key={c.company_key} className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid={`admin-company-${c.email_domain}`}>
            <button
              type="button"
              onClick={() => toggle(c.company_key)}
              aria-expanded={isOpen}
              data-testid={`admin-company-toggle-${c.email_domain}`}
              className="w-full px-4 sm:px-5 py-3 flex items-center justify-between gap-3 hover:bg-zinc-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="h-9 w-9 rounded-sm bg-[#D42B1E]/10 border border-[#D42B1E]/20 text-[#D42B1E] inline-flex items-center justify-center font-semibold text-sm shrink-0">
                  {(c.company || c.email_domain || "?").trim().charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-zinc-900 truncate" title={c.company || c.email_domain}>
                      {c.company || (c.is_personal_mailbox ? "(Compte personnel)" : c.email_domain)}
                    </span>
                    {c.is_personal_mailbox && (
                      <span className="text-[10px] font-mono uppercase tracking-wide text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-sm">
                        personnel
                      </span>
                    )}
                    {c.partner_level && (
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm ${
                        c.partner_level === "Expert" ? "bg-emerald-100 text-emerald-700" :
                        c.partner_level === "Advanced" ? "bg-amber-100 text-amber-800" :
                        c.partner_level === "Select" ? "bg-sky-100 text-sky-700" :
                        "bg-zinc-100 text-zinc-700"
                      }`}>
                        {c.partner_level}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-500 font-mono truncate">@{c.email_domain}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="hidden sm:flex items-center gap-2 text-[11px] font-mono">
                  {c.pending_count > 0 && (
                    <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm">{c.pending_count} en attente</span>
                  )}
                  {c.approved_count > 0 && (
                    <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">{c.approved_count} approuvés</span>
                  )}
                  {c.rejected_count > 0 && (
                    <span className="bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded-sm">{c.rejected_count} refusés</span>
                  )}
                </div>
                <span className="text-xs font-semibold tabular-nums text-zinc-700 bg-zinc-100 px-2 py-1 rounded-sm">
                  {c.member_count} membre{c.member_count > 1 ? "s" : ""}
                  {totalRoles > 1 && ` · ${totalRoles} rôles`}
                </span>
                <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-zinc-100 bg-zinc-50/30 divide-y divide-zinc-100">
                {[
                  { key: "admin", label: "Administrateurs Pythagore", color: "rose" },
                  { key: "reviewer", label: "Reviewers Fortinet", color: "amber" },
                  { key: "partner", label: "Partenaires", color: "sky" },
                ].map((roleGroup) => {
                  const members = c.members_by_role[roleGroup.key] || [];
                  if (!members.length) return null;
                  return (
                    <div key={roleGroup.key} className="p-4">
                      <p className="text-[10px] font-semibold tracking-[0.16em] uppercase text-zinc-500 mb-2">
                        {roleGroup.label}
                        <span className="ml-2 font-mono text-zinc-400 normal-case tracking-normal">{members.length}</span>
                      </p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {members.map((m) => (
                          <div key={m.id} className="bg-white border border-zinc-200 rounded-sm p-3 flex items-center gap-3" data-testid={`company-member-${m.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-zinc-900 truncate">{m.full_name}</p>
                              <p className="text-[11px] font-mono text-zinc-500 truncate">{m.email}</p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                <StatusPill status={m.status} />
                                {m.totp_enabled && (
                                  <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase px-1 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">
                                    <ShieldCheck className="h-2 w-2" /> 2FA
                                  </span>
                                )}
                                {m.partner_level && (
                                  <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm bg-zinc-100 text-zinc-700">
                                    {m.partner_level}
                                  </span>
                                )}
                              </div>
                            </div>
                            {m.totp_enabled && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onReset2FA(m)}
                                disabled={resetting2FAId === m.id}
                                className="rounded-sm h-7 text-[11px] border-rose-200 text-rose-700 hover:bg-rose-50"
                                title="Réinitialiser 2FA"
                                data-testid={`company-member-reset-2fa-${m.id}`}
                              >
                                <ShieldOff className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { bg: "bg-amber-100", text: "text-amber-800", label: "En attente" },
    approved: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Approuvé" },
    rejected: { bg: "bg-zinc-100", text: "text-zinc-600", label: "Refusé" },
  };
  const m = map[status] || map.approved;
  return <span className={`text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded-sm ${m.bg} ${m.text}`}>{m.label}</span>;
}

/**
 * DashboardView — admin pilot KPIs sourced from /api/admin/analytics/overview.
 *
 * Interactive features:
 * - **KPI cards are clickable**: clicking opens a side drawer with the
 *   matching list of users / quotes (sessions actives, devis validés…).
 * - **Top partner rows are expandable**: clicking expands inline to reveal
 *   the company's members + their quote / validation counts.
 * - **Top user rows are expandable**: clicking expands inline to reveal
 *   that user's recent quotes (from `quotes_recent`).
 * - **Recent login rows open a profile drawer** with the user's full info.
 *
 * Auto-refreshes every 60s so the "Sessions actives" counter feels live.
 */
function DashboardView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Side drawer state — `drawer` carries the kind ("active30m", "active24h",
  // "validated", "recent_quotes", "pending_users", "never_logged", "logins")
  // and is null when closed.
  const [drawer, setDrawer] = useState(null);
  // Inline-expanded row trackers — keyed by company_key / user email.
  const [expandedPartners, setExpandedPartners] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/admin/analytics/overview");
      setData(r.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec du chargement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Refresh every 60 seconds so the "Sessions actives" KPI stays fresh
    // without forcing the admin to manually reload the page.
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading && !data) {
    return <div className="p-8 text-center text-sm text-zinc-500">Chargement du tableau de bord…</div>;
  }
  if (error) {
    return <div className="p-8 text-center text-sm text-rose-600 border border-rose-200 bg-rose-50 rounded-sm">{error}</div>;
  }
  if (!data) return null;

  const u = data.users;
  const q = data.quotes;
  const fmt = (v) => (v ?? 0).toLocaleString("fr-FR");

  // Each KPI declares an `onClick` handler — clicking either opens a
  // drill-down drawer (most cases) or no-ops (for derived metrics).
  const KPIS = [
    {
      label: "Inscrits totaux",
      value: u.total,
      sub: `${u.approved} approuvés · ${u.pending} en attente · ${u.rejected} refusés`,
      accent: "rouge",
      onClick: () => setDrawer({ kind: "all_users", title: "Tous les inscrits", items: data.recent_logins }),
    },
    {
      label: "Sessions actives",
      value: u.active_30m,
      sub: "≤ 30 min · live",
      accent: "emerald",
      live: true,
      onClick: () => setDrawer({ kind: "active30m", title: "Sessions actives (≤ 30 min)", items: data.users_active_30m }),
    },
    {
      label: "Actifs 24h",
      value: u.active_24h,
      sub: `${u.never_logged_in} jamais connectés`,
      accent: "amber",
      onClick: () => setDrawer({ kind: "active24h", title: "Utilisateurs actifs (≤ 24h)", items: data.users_active_24h }),
    },
    {
      label: "Devis générés",
      value: q.total,
      sub: `${q.last_7d} cette semaine · ${q.last_30d} ce mois`,
      accent: "rouge",
      onClick: () => setDrawer({ kind: "recent_quotes", title: "Derniers devis générés", items: data.quotes_recent }),
    },
    {
      label: "Devis validés",
      value: q.validated,
      sub: `$${fmt(Math.round(q.validated_amount))} signés`,
      accent: "emerald",
      onClick: () => setDrawer({
        kind: "validated_quotes",
        title: "Devis validés par les clients",
        items: data.quotes_recent.filter((qq) => qq.client_validated),
      }),
    },
    {
      label: "Revues en cours",
      value: q.in_review,
      sub: `${u.reviewers} reviewers Fortinet`,
      accent: "amber",
      onClick: () => setDrawer({
        kind: "in_review",
        title: "Devis en attente de revue Fortinet",
        items: data.quotes_recent.filter((qq) => qq.review_status === "requested"),
      }),
    },
  ];

  const accentClasses = {
    rouge:   "border-l-[#D42B1E] bg-[#D42B1E]/5 hover:bg-[#D42B1E]/10",
    emerald: "border-l-emerald-500 bg-emerald-50 hover:bg-emerald-100",
    amber:   "border-l-amber-500 bg-amber-50 hover:bg-amber-100",
  };

  const togglePartner = (key) =>
    setExpandedPartners((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleUser = (email) =>
    setExpandedUsers((prev) => ({ ...prev, [email]: !prev[email] }));

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Clickable KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {KPIS.map((k) => (
          <button
            key={k.label}
            type="button"
            onClick={k.onClick}
            className={`text-left border border-zinc-200 border-l-4 ${accentClasses[k.accent]} rounded-sm px-4 py-3 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#D42B1E]/30`}
            data-testid={`dashboard-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-center gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-600">
                {k.label}
              </p>
              {k.live && (
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
              )}
              <ChevronRight className="h-3 w-3 text-zinc-400 ml-auto" />
            </div>
            <p className="mt-1 text-3xl font-bold tabular-nums text-zinc-900 leading-none">{fmt(k.value)}</p>
            <p className="mt-1.5 text-[11px] text-zinc-500 leading-snug">{k.sub}</p>
          </button>
        ))}
      </div>

      {/* Two-column expandable tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Top partenaires */}
        <ExpandableTable
          title="Top partenaires"
          subtitle="Cliquez sur une ligne pour voir les membres"
          testId="dashboard-top-partners"
          headers={["", "Partenaire", "Membres", "Devis", "Validés", "Dernier login"]}
          empty="Aucun devis pour l'instant."
          rows={data.by_company.map((c) => ({
            key: c.company_key,
            expanded: !!expandedPartners[c.company_key],
            onClick: () => togglePartner(c.company_key),
            cells: [
              <Caret expanded={!!expandedPartners[c.company_key]} />,
              <PartnerCell company={c.company} domain={c.email_domain} level={c.partner_level} isPersonal={c.is_personal_mailbox} />,
              <span className="font-mono tabular-nums text-zinc-700">{c.member_count}</span>,
              <span className="font-mono tabular-nums font-semibold text-[#D42B1E]">{c.quote_count}</span>,
              <span className="font-mono tabular-nums text-emerald-700">{c.validated_count}</span>,
              <RelativeTime iso={c.last_login_at} />,
            ],
            expandedContent: (
              <PartnerMembersList members={c.members || []} />
            ),
          }))}
        />

        {/* Top utilisateurs */}
        <ExpandableTable
          title="Top utilisateurs"
          subtitle="Cliquez sur une ligne pour voir leurs derniers devis"
          testId="dashboard-top-users"
          headers={["", "Utilisateur", "Société", "Devis", "Validés", "Dernier login"]}
          empty="Aucun utilisateur n'a généré de devis."
          rows={data.by_user.map((u2) => ({
            key: u2.email,
            expanded: !!expandedUsers[u2.email],
            onClick: () => toggleUser(u2.email),
            cells: [
              <Caret expanded={!!expandedUsers[u2.email]} />,
              <div className="min-w-0">
                <div className="font-medium text-zinc-900 truncate" title={u2.full_name}>{u2.full_name || "—"}</div>
                <div className="text-[10px] text-zinc-500 truncate" title={u2.email}>{u2.email}</div>
              </div>,
              <div className="min-w-0">
                <div className="text-zinc-700 truncate" title={u2.company}>{u2.company || "—"}</div>
                {u2.partner_level && (
                  <span className="inline-block text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-[#D42B1E]/10 text-[#D42B1E] border border-[#D42B1E]/20 mt-0.5">
                    {u2.partner_level}
                  </span>
                )}
              </div>,
              <span className="font-mono tabular-nums font-semibold text-[#D42B1E]">{u2.quote_count}</span>,
              <span className="font-mono tabular-nums text-emerald-700">{u2.validated_count}</span>,
              <RelativeTime iso={u2.last_login_at} />,
            ],
            expandedContent: (
              <UserQuotesList quotes={data.quotes_recent.filter((q2) => q2.user_email === u2.email)} />
            ),
          }))}
        />
      </div>

      {/* Recent logins — clickable to open detail drawer */}
      <ExpandableTable
        title="Connexions récentes"
        subtitle="Les 20 dernières · 🟢 = session active (≤ 30 min) · cliquez pour voir le profil"
        testId="dashboard-recent-logins"
        headers={["", "Utilisateur", "Société", "Niveau", "Rôle", "Dernier login", "Dernière activité"]}
        empty="Aucune connexion enregistrée pour le moment."
        rows={data.recent_logins.map((r) => ({
          key: r.email,
          onClick: () => setDrawer({ kind: "user_profile", title: r.full_name || r.email, items: [r] }),
          clickable: true,
          cells: [
            r.active_now ? (
              <span className="relative inline-flex h-2 w-2" title="Session active (≤ 30 min)">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
            ) : (
              <span className="inline-flex h-2 w-2 rounded-full bg-zinc-300" title="Hors session" />
            ),
            <div className="min-w-0">
              <div className="font-medium text-zinc-900 truncate">{r.full_name || "—"}</div>
              <div className="text-[10px] text-zinc-500 truncate">{r.email}</div>
            </div>,
            <span className="text-zinc-700 truncate inline-block max-w-[140px]" title={r.company}>{r.company || "—"}</span>,
            r.partner_level ? (
              <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-[#D42B1E]/10 text-[#D42B1E] border border-[#D42B1E]/20">
                {r.partner_level}
              </span>
            ) : <span className="text-zinc-400">—</span>,
            r.is_admin ? (
              <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-zinc-900 text-white">Admin</span>
            ) : r.is_fortinet_reviewer ? (
              <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-500 text-white">Reviewer</span>
            ) : (
              <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-700 border border-zinc-200">Partner</span>
            ),
            <RelativeTime iso={r.last_login_at} />,
            <RelativeTime iso={r.last_seen_at} />,
          ],
        }))}
      />

      <p className="text-[10px] text-zinc-400 text-right">
        Dernière mise à jour&nbsp;: {new Date(data.generated_at).toLocaleString("fr-FR")} · auto-refresh toutes les 60 s
      </p>

      <DashboardDrawer drawer={drawer} onClose={() => setDrawer(null)} />
    </div>
  );
}

/**
 * ExpandableTable — generic table where each row can declare its own
 * `expandedContent` (rendered inline as a row spanning all columns) or its
 * own `clickable` flag (cursor pointer + onClick handler firing a drawer).
 */
function ExpandableTable({ title, subtitle, headers, rows, empty, testId }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50">
        <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
        {subtitle && <p className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</p>}
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-zinc-500">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-zinc-100">
              <tr className="text-[10px] uppercase tracking-wide text-zinc-500">
                {headers.map((h, i) => (
                  <th key={i} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <React.Fragment key={row.key}>
                  <tr
                    className={`border-b border-zinc-100 ${(row.expandedContent || row.clickable) ? "hover:bg-zinc-100 cursor-pointer" : "hover:bg-zinc-50/50"}`}
                    onClick={row.onClick}
                    data-testid={`${testId}-row-${row.key}`}
                  >
                    {row.cells.map((c, i) => (
                      <td key={i} className="px-3 py-2 align-top">{c}</td>
                    ))}
                  </tr>
                  {row.expanded && row.expandedContent && (
                    <tr className="bg-zinc-50/70 border-b border-zinc-100" data-testid={`${testId}-expanded-${row.key}`}>
                      <td colSpan={headers.length} className="px-4 py-3">
                        {row.expandedContent}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Caret({ expanded }) {
  return expanded ? (
    <ChevronDown className="h-4 w-4 text-zinc-500" />
  ) : (
    <ChevronRight className="h-4 w-4 text-zinc-400" />
  );
}

function PartnerMembersList({ members }) {
  if (!members.length) {
    return <p className="text-xs text-zinc-500 italic">Aucun membre enregistré.</p>;
  }
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-2">
        {members.length} membre{members.length > 1 ? "s" : ""}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {members.map((m) => (
          <div key={m.email} className="flex items-center gap-2 bg-white border border-zinc-200 rounded-sm px-3 py-2 text-xs">
            <RoleBadge role={m.role} />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-zinc-900 truncate" title={m.full_name}>{m.full_name || m.email}</div>
              <div className="text-[10px] text-zinc-500 truncate" title={m.email}>{m.email}</div>
            </div>
            <div className="text-right shrink-0 font-mono">
              <div className="text-[#D42B1E] font-semibold tabular-nums">{m.quote_count}</div>
              <div className="text-[9px] text-zinc-400 uppercase tracking-wide">devis</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UserQuotesList({ quotes }) {
  if (!quotes.length) {
    return <p className="text-xs text-zinc-500 italic">Aucun devis récent.</p>;
  }
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 mb-2">
        {quotes.length} devis dans les 50 derniers
      </p>
      <div className="space-y-1.5">
        {quotes.map((q) => (
          <div key={q.id} className="flex items-center gap-2 bg-white border border-zinc-200 rounded-sm px-3 py-2 text-xs">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-zinc-900 truncate" title={q.title}>{q.title}</div>
              <div className="text-[10px] text-zinc-500 truncate">
                {new Date(q.created_at).toLocaleDateString("fr-FR")}
                {q.client_validated && (
                  <span className="ml-2 inline-flex items-center gap-0.5 text-emerald-700 font-semibold">
                    <CheckCircle2 className="h-2.5 w-2.5" /> Validé
                  </span>
                )}
                {q.review_status === "requested" && (
                  <span className="ml-2 text-amber-700 font-semibold">En revue</span>
                )}
              </div>
            </div>
            <span className="font-mono font-semibold text-zinc-900 tabular-nums">${q.total_cost.toLocaleString("fr-FR")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const map = {
    admin: { bg: "bg-zinc-900", text: "text-white", label: "A" },
    reviewer: { bg: "bg-amber-500", text: "text-white", label: "R" },
    partner: { bg: "bg-zinc-200", text: "text-zinc-700", label: "P" },
  };
  const m = map[role] || map.partner;
  return (
    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-sm font-bold text-[10px] ${m.bg} ${m.text} shrink-0`} title={role}>
      {m.label}
    </span>
  );
}

/**
 * DashboardDrawer — side panel that opens on KPI card click. Renders a
 * different table layout depending on `drawer.kind` (users vs quotes vs
 * single-profile detail).
 */
function DashboardDrawer({ drawer, onClose }) {
  const navigate = useNavigate();
  const isQuotes = drawer && ["recent_quotes", "validated_quotes", "in_review"].includes(drawer.kind);
  const isProfile = drawer && drawer.kind === "user_profile";
  return (
    <Sheet open={!!drawer} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="dashboard-drawer">
        <SheetHeader>
          <SheetTitle className="text-lg">{drawer?.title}</SheetTitle>
          {drawer && (
            <SheetDescription>
              {drawer.items.length === 0 ? "Aucune entrée" : `${drawer.items.length} entrée${drawer.items.length > 1 ? "s" : ""}`}
            </SheetDescription>
          )}
        </SheetHeader>

        {drawer && drawer.items.length === 0 && (
          <div className="mt-6 p-8 text-center text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-sm">
            Aucun résultat à afficher.
          </div>
        )}

        {isProfile && drawer.items[0] && (
          <ProfileDetail profile={drawer.items[0]} />
        )}

        {!isProfile && drawer && drawer.items.length > 0 && !isQuotes && (
          <div className="mt-6 space-y-2">
            {drawer.items.map((u) => (
              <div key={u.email} className="border border-zinc-200 bg-white rounded-sm p-3 flex items-center gap-3">
                {u.active_now ? (
                  <span className="relative inline-flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                ) : (
                  <span className="inline-flex h-2 w-2 rounded-full bg-zinc-300 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-zinc-900 truncate">{u.full_name || u.email}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{u.email}</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {u.company && <span className="text-[10px] text-zinc-600">{u.company}</span>}
                    {u.partner_level && (
                      <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-[#D42B1E]/10 text-[#D42B1E] border border-[#D42B1E]/20">
                        {u.partner_level}
                      </span>
                    )}
                    {u.is_admin && <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-zinc-900 text-white">Admin</span>}
                    {u.is_fortinet_reviewer && !u.is_admin && <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-500 text-white">Reviewer</span>}
                  </div>
                </div>
                <div className="text-right text-[10px] text-zinc-500 shrink-0">
                  <div>Login</div>
                  <RelativeTime iso={u.last_login_at} />
                </div>
              </div>
            ))}
          </div>
        )}

        {isQuotes && drawer.items.length > 0 && (
          <div className="mt-6 space-y-2">
            {drawer.items.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => { onClose(); navigate(`/quotes?id=${q.id}`); }}
                className="w-full text-left border border-zinc-200 bg-white rounded-sm p-3 hover:border-[#D42B1E]/40 hover:bg-[#D42B1E]/5 transition-colors"
                data-testid={`drawer-quote-${q.id}`}
              >
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-zinc-900 truncate">{q.title}</div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {q.user_full_name || q.user_email || "—"}
                      {q.user_company && ` · ${q.user_company}`}
                    </div>
                  </div>
                  <span className="font-mono font-semibold text-zinc-900 tabular-nums shrink-0">
                    ${q.total_cost.toLocaleString("fr-FR")}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px]">
                  <span className="text-zinc-400">{new Date(q.created_at).toLocaleString("fr-FR")}</span>
                  {q.client_validated && (
                    <span className="inline-flex items-center gap-0.5 text-emerald-700 font-semibold uppercase tracking-wide">
                      <CheckCircle2 className="h-2.5 w-2.5" /> Validé
                    </span>
                  )}
                  {q.review_status === "requested" && (
                    <span className="text-amber-700 font-semibold uppercase tracking-wide">En revue</span>
                  )}
                  <ExternalLink className="h-3 w-3 text-zinc-400 ml-auto" />
                </div>
              </button>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ProfileDetail({ profile }) {
  return (
    <div className="mt-6 space-y-3">
      <div className="border border-zinc-200 bg-white rounded-sm p-4">
        <div className="flex items-center gap-3">
          {profile.active_now ? (
            <span className="relative inline-flex h-3 w-3 shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
            </span>
          ) : (
            <span className="inline-flex h-3 w-3 rounded-full bg-zinc-300 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <h4 className="font-semibold text-zinc-900">{profile.full_name || "—"}</h4>
            <p className="text-sm text-zinc-600">{profile.email}</p>
          </div>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div>
            <dt className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500">Société</dt>
            <dd className="text-zinc-900 mt-0.5">{profile.company || "—"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500">Niveau partenaire</dt>
            <dd className="mt-0.5">
              {profile.partner_level ? (
                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-[#D42B1E]/10 text-[#D42B1E] border border-[#D42B1E]/20">
                  {profile.partner_level}
                </span>
              ) : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500">Rôle</dt>
            <dd className="mt-0.5">
              {profile.is_admin ? (
                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-zinc-900 text-white">Admin</span>
              ) : profile.is_fortinet_reviewer ? (
                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-500 text-white">Reviewer</span>
              ) : (
                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-700 border border-zinc-200">Partner</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500">Statut</dt>
            <dd className="mt-0.5"><StatusPill status={profile.status} /></dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500">Dernier login</dt>
            <dd className="text-zinc-900 mt-0.5">
              <RelativeTime iso={profile.last_login_at} />
              {profile.last_login_at && (
                <div className="text-[10px] text-zinc-400 mt-0.5">{new Date(profile.last_login_at).toLocaleString("fr-FR")}</div>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500">Dernière activité</dt>
            <dd className="text-zinc-900 mt-0.5">
              <RelativeTime iso={profile.last_seen_at} />
              {profile.last_seen_at && (
                <div className="text-[10px] text-zinc-400 mt-0.5">{new Date(profile.last_seen_at).toLocaleString("fr-FR")}</div>
              )}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

function PartnerCell({ company, domain, level, isPersonal }) {
  return (
    <div className="min-w-0">
      <div className="font-medium text-zinc-900 truncate" title={company || domain}>
        {isPersonal ? "Compte personnel" : (company || domain)}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span className="text-[10px] font-mono text-zinc-500 truncate" title={`@${domain}`}>@{domain}</span>
        {level && (
          <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-[#D42B1E]/10 text-[#D42B1E] border border-[#D42B1E]/20">
            {level}
          </span>
        )}
      </div>
    </div>
  );
}

function RelativeTime({ iso }) {
  if (!iso) return <span className="text-zinc-400 text-xs">jamais</span>;
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  let label;
  if (min < 1) label = "à l'instant";
  else if (min < 60) label = `il y a ${min} min`;
  else if (hr < 24) label = `il y a ${hr} h`;
  else if (day < 7) label = `il y a ${day} j`;
  else label = new Date(iso).toLocaleDateString("fr-FR");
  return (
    <span className="text-xs text-zinc-600 whitespace-nowrap" title={new Date(iso).toLocaleString("fr-FR")}>
      {label}
    </span>
  );
}
